<template>
  <button
    :class="buttonClasses"
    :disabled="disabled || loading"
    :aria-label="ariaLabel"
    :aria-describedby="error ? `${id}-error` : undefined"
    :id="id"
    @click="$emit('click', $event)"
    @keydown.enter="$emit('click', $event)"
    @keydown.space.prevent="$emit('click', $event)"
  >
    <span v-if="loading" class="inline-flex items-center">
      <svg
        class="animate-spin -ml-1 mr-2 h-4 w-4"
        fill="none"
        viewBox="0 0 24 24"
        aria-hidden="true"
      >
        <circle
          class="opacity-25"
          cx="12"
          cy="12"
          r="10"
          stroke="currentColor"
          stroke-width="4"
        ></circle>
        <path
          class="opacity-75"
          fill="currentColor"
          d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
        ></path>
      </svg>
      <span class="sr-only">Loading...</span>
      <span aria-hidden="true">{{ loadingText }}</span>
    </span>
    <span v-else-if="icon && iconPosition === 'left'" class="inline-flex items-center">
      <component :is="icon" class="w-4 h-4 mr-2" aria-hidden="true" />
      <slot>{{ label }}</slot>
    </span>
    <span v-else-if="icon && iconPosition === 'right'" class="inline-flex items-center">
      <slot>{{ label }}</slot>
      <component :is="icon" class="w-4 h-4 ml-2" aria-hidden="true" />
    </span>
    <span v-else>
      <slot>{{ label }}</slot>
    </span>
  </button>
</template>

<script setup lang="ts">
import { computed, type PropType } from 'vue'

type ButtonVariant = 'primary' | 'secondary' | 'danger' | 'success' | 'outline'
type ButtonSize = 'sm' | 'md' | 'lg'
type IconPosition = 'left' | 'right'

const props = defineProps({
  variant: {
    type: String as PropType<ButtonVariant>,
    default: 'primary',
    validator: (value: string) =>
      ['primary', 'secondary', 'danger', 'success', 'outline'].includes(value),
  },
  size: {
    type: String as PropType<ButtonSize>,
    default: 'md',
    validator: (value: string) => ['sm', 'md', 'lg'].includes(value),
  },
  disabled: {
    type: Boolean,
    default: false,
  },
  loading: {
    type: Boolean,
    default: false,
  },
  loadingText: {
    type: String,
    default: 'Loading...',
  },
  label: {
    type: String,
    default: '',
  },
  ariaLabel: {
    type: String,
    default: '',
  },
  fullWidth: {
    type: Boolean,
    default: false,
  },
  icon: {
    type: [Object, Function] as PropType<any>, // Vue component type
    default: null,
  },
  iconPosition: {
    type: String as PropType<IconPosition>,
    default: 'left',
    validator: (value: string) => ['left', 'right'].includes(value),
  },
  id: {
    type: String,
    default: '',
  },
  error: {
    type: Boolean,
    default: false,
  },
})

defineEmits<{
  (_e: 'click', event: Event): void
}>()

const buttonClasses = computed<string>(() => {
  const baseClasses = [
    'inline-flex items-center justify-center font-semibold rounded-lg transition-all duration-200',
    'focus:outline-none focus:ring-2 focus:ring-offset-2',
    'disabled:opacity-60 disabled:cursor-not-allowed',
  ]

  // Size classes
  const sizeClasses: Record<ButtonSize, string> = {
    sm: 'px-3 py-1.5 text-xs',
    md: 'px-4 py-2 text-sm',
    lg: 'px-5 py-2.5 text-base',
  }

  // Variant classes
  const variantClasses: Record<ButtonVariant, string> = {
    primary:
      'bg-teal-600 hover:bg-teal-700 text-white focus:ring-teal-500 dark:focus:ring-offset-gray-900',
    secondary:
      'bg-gray-200 hover:bg-gray-300 text-gray-800 focus:ring-teal-500 dark:bg-gray-700 dark:hover:bg-gray-600 dark:text-gray-100 dark:focus:ring-offset-gray-900',
    danger:
      'bg-red-600 hover:bg-red-700 text-white focus:ring-red-500 dark:focus:ring-offset-gray-900',
    success:
      'bg-green-600 hover:bg-green-700 text-white focus:ring-green-500 dark:focus:ring-offset-gray-900',
    outline:
      'border border-gray-300 bg-white hover:bg-gray-50 text-gray-700 focus:ring-teal-500 dark:bg-gray-900 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-800 dark:focus:ring-offset-gray-900',
  }

  const classes = [...baseClasses, sizeClasses[props.size], variantClasses[props.variant]]

  if (props.fullWidth) {
    classes.push('w-full')
  }

  return classes.join(' ')
})
</script>
